/* eslint-disable react/no-unescaped-entities */
import React from "react";
import {Grid, Card, CardContent, Chip, Typography, Alert, Box, useMediaQuery, TextField, MenuItem, Button, IconButton, Tooltip, Divider, Stack, Select, InputLabel, FormControl, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Menu, Avatar, Link, CircularProgress, Tabs, Tab, Badge} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import properties from "constants/ApplicationConstants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import {GET, PUT, POST, BASE_URL} from "APIclient";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import SortIcon from "@mui/icons-material/Sort";
import SendIcon from "@mui/icons-material/Send";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CommentIcon from "@mui/icons-material/Comment";
import DialogContentText from "@mui/material/DialogContentText";

function StatusColumn({title, tickets, emptyText, color, onDragOver, onDrop, makeDraggable, onPriorityChange}) {
  const parseComments = (commentsJson) => {
    if (!commentsJson) return [];
    try {
      const parsed = typeof commentsJson === 'string' ? JSON.parse(commentsJson) : commentsJson;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error("Error parsing comments:", e);
      return [];
    }
  };

  return (
    <Grid item xs={12} md={4}>
      <Card sx={{height: "100%"}} onDragOver={onDragOver} onDrop={onDrop}>
        <CardContent>
          <MDTypography variant="h6" sx={{fontFamily: properties.fontFamily, fontSize: "16px", mb: 1, display: "flex", alignItems: "center", gap: 1}}>
            <Chip size="small" label={tickets.length} color={color} /> {title}
          </MDTypography>
          {tickets.length === 0 ? (
            <Alert severity="info">{emptyText}</Alert>
          ) : (
            <Grid container spacing={1}>
              {tickets.map(ticket => (
                <Grid item xs={12} key={ticket.ticketId}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2, 
                      cursor: makeDraggable ? "grab" : "default",
                      borderLeft: (ticket.__isReassigned || ticket.__isReopened || ticket.__isRecent) ? 4 : 1,
                      borderLeftColor: ticket.__isReassigned ? "#FFE082" : // Very light orange/yellow
                                       ticket.__isReopened ? "#F8BBD0" : // Very light pink
                                       ticket.__isRecent ? "#B3E5FC" : // Very light blue
                                       "divider",
                      backgroundColor: (ticket.__isReassigned || ticket.__isReopened || ticket.__isRecent) ? 
                                       (ticket.__isReassigned ? "rgba(255, 224, 130, 0.04)" : // Very light yellow background
                                        ticket.__isReopened ? "rgba(248, 187, 208, 0.04)" : // Very light pink background
                                        "rgba(179, 229, 252, 0.04)") : // Very light blue background
                                       "transparent",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: 2
                      }
                    }}
                    draggable={makeDraggable}
                    onDragStart={e => {
                      if (!makeDraggable) return;
                      e.dataTransfer.setData("application/ticket-id", String(ticket.ticketId));
                    }}
                  >
                    <CardContent sx={{pb: 1.5}}>
                      <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5}}>
                        <Typography sx={{fontWeight: 600, fontSize: 14}}>#{ticket.ticketId} • {ticket.title}</Typography>
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                          <Tooltip title={`Priority: ${ticket.priority}`}>
                            <Box>
                              {ticket.priority === "HIGH" ? (
                                <ArrowUpwardIcon sx={{ fontSize: 18, color: "error.main" }} />
                              ) : ticket.priority === "MEDIUM" ? (
                                <RemoveIcon sx={{ fontSize: 18, color: "warning.main" }} />
                              ) : (
                                <ArrowDownwardIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                              )}
                            </Box>
                          </Tooltip>
                          {onPriorityChange && (
                            <Tooltip title="Change priority">
                              <IconButton
                                size="small"
                                onClick={e => {
                                  e.stopPropagation();
                                  ticket.__onPriorityChange?.(e, ticket);
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                      <Typography sx={{fontSize: 12, color: "text.secondary", mb: 0.2}}>
                        By {ticket.empName} ({ticket.empId}) • Asset ID: #{ticket.assetId ?? "—"}
                      </Typography>
                      <Typography sx={{fontSize: 12, color: "text.secondary", mb: 0.5}}>
                        Asset No: {ticket.assetNo || "—"}
                      </Typography>
                      <Box sx={{display:"flex", justifyContent:"space-between", alignItems:"center", mt: 0.5}}>
                        <Box sx={{display:"flex", alignItems:"center", gap: 1}}>
                          {(() => {
                            const currentEmpId = window.sessionStorage.getItem("empId");
                            const parsedComments = parseComments(ticket.commentsJson);
                            const commentCount = parsedComments.length;
                            const readCommentsKey = `readComments_${ticket.ticketId}_${currentEmpId}`;
                            const readComments = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
                            const unreadCommentCount = parsedComments.filter(c => 
                              String(c.senderId) !== String(currentEmpId) && 
                              !readComments.includes(c.timestamp)
                            ).length;
                            return (
                              <Tooltip title={unreadCommentCount > 0 ? `${unreadCommentCount} unread comment${unreadCommentCount !== 1 ? 's' : ''}` : commentCount > 0 ? `${commentCount} comment${commentCount !== 1 ? 's' : ''} (all read)` : "View comments"}>
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (ticket.__onViewComments) {
                                      ticket.__onViewComments(ticket);
                                    }
                                  }}
                                  sx={{ 
                                    p: 0.5,
                                    color: unreadCommentCount > 0 ? "error.main" : "text.secondary"
                                  }}
                                >
                                  <Badge 
                                    badgeContent={unreadCommentCount > 0 ? unreadCommentCount : (commentCount > 0 ? commentCount : 0)} 
                                    color={unreadCommentCount > 0 ? "error" : "default"}
                                    sx={{
                                      '& .MuiBadge-badge': {
                                        fontSize: '10px',
                                        height: '18px',
                                        minWidth: '18px',
                                        fontWeight: 600
                                      }
                                    }}
                                  >
                                    <CommentIcon sx={{ fontSize: 18 }} />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                            );
                          })()}
                        </Box>
                        <Tooltip title="View details">
                          <IconButton size="small" color="primary" onClick={() => ticket.__onView(ticket)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Divider sx={{my: 1}} />
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {ticket.__isRecent && <Chip size="small" label="NEW" sx={{ fontWeight: 600, bgcolor: "#E3F2FD", color: "#1976D2" }} />}
                        {ticket.__isReassigned && <Chip size="small" label="REASSIGNED" sx={{ fontWeight: 600, bgcolor: "#FFF9C4", color: "#F57F17" }} />}
                        {ticket.__isReopened && <Chip size="small" label="REOPENED" sx={{ fontWeight: 600, bgcolor: "#FCE4EC", color: "#C2185B" }} />}
                        {ticket.assignedToEmpName && <Chip size="small" variant="outlined" label={`Assigned To: ${ticket.assignedToEmpName}`} />}
                        <Chip size="small" variant="outlined" label={`Created on: ${ticket.createdOn ? dayjs(ticket.createdOn).format("DD MMM YYYY HH:mm") : "—"}`} />
                        {String(ticket.status||"").toUpperCase()==="IN_PROGRESS" && (ticket.lastModifiedOn || ticket.updatedOn) && (
                          <Chip size="small" variant="outlined" label={`Updated: ${dayjs(ticket.lastModifiedOn || ticket.updatedOn).format("DD MMM YYYY HH:mm")}`} />
                        )}
                        {ticket.resolvedOn && <Chip size="small" variant="outlined" label={`Resolved: ${dayjs(ticket.resolvedOn).format("DD MMM YYYY HH:mm")}`} />}
                        {ticket.resolutionComment && <Chip size="small" variant="outlined" label={`Resolution: ${ticket.resolutionComment}`}/>}
                        {ticket.reopenReason && <Chip size="small" variant="outlined" label={`Reopen Reason: ${ticket.reopenReason}`} sx={{ borderColor: "#FFE082", color: "#F57F17" }}/>}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
}

export default function ITHelpdesk() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [tickets, setTickets] = React.useState([]);
  const [filters, setFilters] = React.useState({
    empId: "",
    empName: "",
    assetId: "",
    assetNo: "",
    status: "",
    priority: "",
    assignedToEmpId: "",
    page: 0,
    size: 50
  });
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [selectedTicket, setSelectedTicket] = React.useState(null);
  const [commentsDialogOpen, setCommentsDialogOpen] = React.useState(false);
  const [selectedTicketForComments, setSelectedTicketForComments] = React.useState(null);
  const [closeDialogOpen, setCloseDialogOpen] = React.useState(false);
  const [ticketToClose, setTicketToClose] = React.useState(null);
  const [resolutionComment, setResolutionComment] = React.useState("");
  const [snackbar, setSnackbar] = React.useState({open: false, message: "", severity: "success"});
  const [priorityMenuAnchor, setPriorityMenuAnchor] = React.useState(null);
  const [ticketForPriorityChange, setTicketForPriorityChange] = React.useState(null);
  const [comments, setComments] = React.useState([]);
  const [newComment, setNewComment] = React.useState("");
  const [commentLoading, setCommentLoading] = React.useState(false);
  const [sortOrder, setSortOrder] = React.useState("newest");
  const [employeeNamesCache, setEmployeeNamesCache] = React.useState({});
  const [unreadCommentCount, setUnreadCommentCount] = React.useState(0);
  const [lastReadCommentId, setLastReadCommentId] = React.useState(null);
  const [reassignDialogOpen, setReassignDialogOpen] = React.useState(false);
  const [reassignLoading, setReassignLoading] = React.useState(false);
  const [employeesList, setEmployeesList] = React.useState([]);
  const [selectedNewEmployeeId, setSelectedNewEmployeeId] = React.useState("");
  const [commentsReadRefresh, setCommentsReadRefresh] = React.useState(0);
  // Track reassignments locally since backend doesn't return reassignment fields
  const [reassignedTickets, setReassignedTickets] = React.useState(() => {
    try {
      const stored = localStorage.getItem('itHelpdesk_reassignedTickets');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const fetchTickets = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const currentEmpId = window.sessionStorage.getItem("empId");
      // Build query string from provided filters
      const params = [];
      if (filters.empId) params.push(`empId=${encodeURIComponent(filters.empId)}`);
      if (filters.status) {
        const statusValue = String(filters.status).toUpperCase() === "CLOSED" ? "DONE" : filters.status;
        params.push(`status=${encodeURIComponent(statusValue)}`);
      }
      if (filters.empName && filters.empName.trim()) params.push(`empName=${encodeURIComponent(filters.empName.trim())}`);
      if (filters.assetId) params.push(`assetId=${encodeURIComponent(filters.assetId)}`);
      if (filters.assetNo) params.push(`assetNo=${encodeURIComponent(filters.assetNo)}`);
      if (filters.priority) params.push(`priority=${encodeURIComponent(filters.priority)}`);
      if (filters.assignedToEmpId) params.push(`assignedToEmpId=${encodeURIComponent(filters.assignedToEmpId)}`);
      if (filters.page !== undefined && filters.page !== null) params.push(`page=${encodeURIComponent(filters.page)}`);
      if (filters.size) params.push(`size=${encodeURIComponent(filters.size)}`);
      const query = params.length ? `?${params.join("&")}` : "";
      const res = await GET(`it-dept/tickets${query}`);
      if (res?.data?.statusCode === 200) {
        const list = res.data?.data?.paginatedData || [];
        const now = Date.now();
        const oneDayAgo = now - 24*60*60*1000;
        
        const processedTickets = list.map(t => {
          const createdTime = t?.createdOn ? new Date(t.createdOn).getTime() : 0;
          const updatedTime = t?.updatedOn || t?.lastModifiedOn ? new Date(t.updatedOn || t.lastModifiedOn).getTime() : 0;
          const resolvedTime = t?.resolvedOn ? new Date(t.resolvedOn).getTime() : 0;
          const status = String(t?.status || "").toUpperCase();
          
          const isRecentlyCreated = createdTime > oneDayAgo;
          
          // Detect reassignment: Show "REASSIGNED" tag only when ticket was reassigned
          // from one IT admin to another IT admin
          // Since backend doesn't return reassignment fields, check local tracking
          const ticketReassignment = reassignedTickets[t.ticketId];
          
          // Check if ticket was reassigned:
          // 1. Has local reassignment record
          // 2. Currently assigned to an IT admin
          // 3. Status is not OPEN (reassignments only happen for IN_PROGRESS or DONE tickets)
          // 4. Current assignedToEmpId matches the newAssignedToEmpId from reassignment record
          //    (to ensure the tag only shows for the reassigned ticket, not if it was reassigned again)
          const isReassigned = ticketReassignment != null &&
                              t?.assignedToEmpId && 
                              status !== "OPEN" && 
                              String(t.assignedToEmpId) === String(ticketReassignment.newAssignedToEmpId) &&
                              String(ticketReassignment.previousAssignedToEmpId) !== String(ticketReassignment.newAssignedToEmpId);
          
          const isRecentlyReopened = t?.reopenReason && updatedTime > oneDayAgo;
          
          // Calculate comment counts
          const parsedComments = parseComments(t.commentsJson);
          const commentCount = parsedComments.length;
          const readCommentsKey = `readComments_${t.ticketId}_${currentEmpId}`;
          const readComments = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
          const unreadCommentCount = parsedComments.filter(c => 
            String(c.senderId) !== String(currentEmpId) && 
            !readComments.includes(c.timestamp)
          ).length;
          
          return {
            ...t,
            __isRecent: isRecentlyCreated,
            __isReassigned: isReassigned,
            __isReopened: isRecentlyReopened,
            __priorityScore: (isRecentlyCreated ? 3 : 0) + (isReassigned ? 2 : 0) + (isRecentlyReopened ? 2 : 0),
            __sortTime: Math.max(createdTime, updatedTime, resolvedTime),
            __commentCount: commentCount,
            __unreadCommentCount: unreadCommentCount
          };
        });
        
        // Sort: priority score first (recently raised, reassigned, reopened), then by most recent activity
        const sorted = processedTickets.sort((a, b) => {
          if (a.__priorityScore !== b.__priorityScore) {
            return b.__priorityScore - a.__priorityScore;
          }
          return b.__sortTime - a.__sortTime;
        });
        
        setTickets(sorted);
      } else {
        setError(res?.data?.message || "Failed to fetch tickets");
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to fetch tickets");
    } finally {
      setLoading(false);
    }
  }, [filters, reassignedTickets]);

  React.useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const todo = tickets.filter(t => (t.status || "").toUpperCase() === "OPEN");
  const inProgress = tickets.filter(t => (t.status || "").toUpperCase() === "IN_PROGRESS");
  const done = tickets.filter(t => ["RESOLVED", "DONE", "CLOSED"].includes((t.status || "").toUpperCase()));

  const handleDragOver = e => {
    e.preventDefault();
  };

  const handleDropToInProgress = async e => {
    e.preventDefault();
    const ticketId = e.dataTransfer.getData("application/ticket-id");
    if (!ticketId) return;
    const empId = window.sessionStorage.getItem("empId");
    try {
      await PUT(`it-dept/tickets/${ticketId}/pick?empId=${empId}`);
      await fetchTickets();
      setSnackbar({open: true, message: "Ticket picked successfully", severity: "success"});
    } catch (err) {
      console.error(err);
      setSnackbar({open: true, message: err?.response?.data?.message || "Failed to pick ticket", severity: "error"});
    }
  };

  const handleDropToOpen = async e => {
    e.preventDefault();
    const ticketId = e.dataTransfer.getData("application/ticket-id");
    if (!ticketId) return;
    const ticket = tickets.find(t => String(t.ticketId) === ticketId);
    if (ticket && (ticket.status || "").toUpperCase() === "IN_PROGRESS") {
      try {
        const response = await PUT(`it-dept/tickets/${ticketId}/movebacktoopen`);
        if (response?.data?.statusCode === 200) {
          setSnackbar({open: true, message: response.data.message || "Ticket moved back to OPEN successfully", severity: "success"});
          await fetchTickets();
        } else {
          setSnackbar({open: true, message: response?.data?.message || "Failed to move ticket back to OPEN", severity: "error"});
        }
      } catch (err) {
        console.error(err);
        setSnackbar({open: true, message: err?.response?.data?.message || "Failed to move ticket back to OPEN", severity: "error"});
      }
    } else {
      setSnackbar({
        open: true,
        message: "Only tickets in IN_PROGRESS state can be moved back to OPEN",
        severity: "error"
      });
    }
  };

  const handleDropToDone = e => {
    e.preventDefault();
    const ticketId = e.dataTransfer.getData("application/ticket-id");
    if (!ticketId) return;
    // Find the ticket to get its details
    const ticket = tickets.find(t => String(t.ticketId) === ticketId);
    const currentEmpId = window.sessionStorage.getItem("empId");
    if (ticket && (ticket.status || "").toUpperCase() === "IN_PROGRESS") {
      // Only allow closing if the current user is the assigned IT admin
      if (ticket.assignedToEmpId && String(ticket.assignedToEmpId) === String(currentEmpId)) {
        setTicketToClose(ticket);
        setResolutionComment("");
        setCloseDialogOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: "Only the assigned IT admin can close this ticket",
          severity: "error"
        });
      }
    }
  };

  const handleCloseTicket = async () => {
    if (!ticketToClose || !resolutionComment.trim()) {
      setSnackbar({open: true, message: "Please enter a resolution comment", severity: "error"});
      return;
    }

    // Check if current user is the assigned IT admin
    const currentEmpId = window.sessionStorage.getItem("empId");
    if (ticketToClose.assignedToEmpId && String(ticketToClose.assignedToEmpId) !== String(currentEmpId)) {
      setSnackbar({
        open: true,
        message: "Only the assigned IT admin can close this ticket",
        severity: "error"
      });
      return;
    }

    try {
      const response = await PUT(`it-dept/tickets/${ticketToClose.ticketId}/close`, {
        resolutionComment: resolutionComment.trim()
      });
      
      if (response?.data?.statusCode === 200) {
        setSnackbar({open: true, message: response.data.message || "Ticket closed successfully", severity: "success"});
        setCloseDialogOpen(false);
        setTicketToClose(null);
        setResolutionComment("");
        await fetchTickets();
      } else {
        setSnackbar({open: true, message: response?.data?.message || "Failed to close ticket", severity: "error"});
      }
    } catch (err) {
      console.error(err);
      setSnackbar({open: true, message: err?.response?.data?.message || "Failed to close ticket", severity: "error"});
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({...prev, open: false}));
  };

  const handlePriorityMenuOpen = (e, ticket) => {
    e.stopPropagation();
    setPriorityMenuAnchor(e.currentTarget);
    setTicketForPriorityChange(ticket);
  };

  const handlePriorityMenuClose = () => {
    setPriorityMenuAnchor(null);
    setTicketForPriorityChange(null);
  };

  const handleChangePriority = async (priority) => {
    if (!ticketForPriorityChange) return;

    try {
      const response = await PUT(`it-dept/tickets/${ticketForPriorityChange.ticketId}/priority?priority=${priority}`);
      
      if (response?.data?.statusCode === 200) {
        setSnackbar({open: true, message: response.data.message || "Ticket priority updated successfully", severity: "success"});
        handlePriorityMenuClose();
        await fetchTickets();
      } else {
        setSnackbar({open: true, message: response?.data?.message || "Failed to update priority", severity: "error"});
      }
    } catch (err) {
      console.error(err);
      setSnackbar({open: true, message: err?.response?.data?.message || "Failed to update priority", severity: "error"});
    }
  };

  const empId = window.sessionStorage.getItem("empId");
  const empName = window.sessionStorage.getItem("EmpName") || "";

  // Fetch IT admin employees for reassign dropdown using /it-dept/it-admins API
  React.useEffect(() => {
    const fetchITAdmins = async () => {
      try {
        const response = await GET("it-dept/it-admins");
        const data = response?.data?.data || [];
        
        console.log("IT Admins fetched from API:", data.length);
        
        // Map the API response to the expected format
        // API returns: { empId, username }
        // Extract name from username (part before @) or use username
        const normalizedEmployees = data.map(admin => {
          const username = admin.username || "";
          // Extract name from email (part before @) and capitalize first letter of each word
          const nameFromEmail = username.split("@")[0] || username;
          const formattedName = nameFromEmail
            .split(".")
            .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(" ");
          
          return {
            id: admin.empId,
            empId: admin.empId,
            employeeName: formattedName || username,
            username: username
          };
        });
        
        console.log(`✅ Found ${normalizedEmployees.length} IT admins for reassign dropdown:`, normalizedEmployees.map(e => `${e.employeeName} (#${e.empId})`));
        setEmployeesList(normalizedEmployees);
      } catch (error) {
        console.error("❌ Error fetching IT admins:", error);
        setEmployeesList([]);
      }
    };
    fetchITAdmins();
  }, []);

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name) return "#1976d2";
    const colors = [
      "#1976d2", "#388e3c", "#f57c00", "#7b1fa2", "#c2185b",
      "#0288d1", "#00796b", "#5d4037", "#455a64", "#d32f2f"
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const parseComments = (commentsJson) => {
    if (!commentsJson) return [];
    try {
      const parsed = typeof commentsJson === 'string' ? JSON.parse(commentsJson) : commentsJson;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error("Error parsing comments:", e);
      return [];
    }
  };

  const [previousTicketId, setPreviousTicketId] = React.useState(null);

  React.useEffect(() => {
    if (selectedTicketForComments) {
      const parsedComments = parseComments(selectedTicketForComments.commentsJson);
      setComments(parsedComments);
      const readCommentsKey = `readComments_${selectedTicketForComments.ticketId}_${empId}`;
      const readComments = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
      const unreadCount = parsedComments.filter(c => 
        String(c.senderId) !== String(empId) && 
        !readComments.includes(c.timestamp)
      ).length;
      setUnreadCommentCount(unreadCount);
      if (parsedComments.length > 0) {
        setLastReadCommentId(parsedComments[parsedComments.length - 1].timestamp);
      }
    } else if (!commentsDialogOpen) {
      setComments([]);
      setNewComment("");
      setUnreadCommentCount(0);
      setLastReadCommentId(null);
    }
  }, [selectedTicketForComments, empId, commentsDialogOpen]);

  // Pre-fetch employee names for all comment senders
  React.useEffect(() => {
    if (!comments || comments.length === 0) return;

    const fetchMissingNames = async () => {
      // Get unique sender IDs that need names
      const uniqueSenderIds = [...new Set(comments.map(c => String(c.senderId)))];
      
      // Filter out sender IDs that:
      // 1. Already have cached names (check current cache state)
      // 2. Match current user (we already have empName)
      // 3. Match ticket employee (we have ticket.empName)
      // 4. Match assigned IT admin (we have ticket.assignedToEmpName)
      setEmployeeNamesCache(prevCache => {
        const missingSenderIds = uniqueSenderIds.filter(senderId => {
          if (String(senderId) === String(empId)) return false; // Current user
          const ticket = selectedTicketForComments || selectedTicket;
          if (ticket && String(senderId) === String(ticket.empId)) return false; // Ticket employee
          if (ticket && String(senderId) === String(ticket.assignedToEmpId)) return false; // Assigned IT admin
          if (prevCache[senderId]) return false; // Already cached
          return true;
        });

        // Fetch names for missing sender IDs
        missingSenderIds.forEach(async (senderId) => {
          try {
            const response = await GET(`employee/employee-details/${senderId}`);
            if (response?.data?.statusCode === 200) {
              const employee = response.data.data?.employee;
              const name = employee?.employeeName || employee?.empName || 
                          `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() ||
                          `Employee ${senderId}`;
              setEmployeeNamesCache(prev => {
                if (prev[senderId]) return prev; // Already set by another fetch
                return { ...prev, [senderId]: name };
              });
            }
          } catch (err) {
            console.error(`Error fetching employee name for ${senderId}:`, err);
            // Set a fallback name in cache to avoid repeated failed requests
            setEmployeeNamesCache(prev => {
              if (prev[senderId]) return prev;
              return { ...prev, [senderId]: `User ${senderId}` };
            });
          }
        });

        return prevCache; // Return unchanged cache
      });
    };

    fetchMissingNames();
  }, [comments, empId, selectedTicketForComments]);

  // Real-time comment polling when comments dialog is open
  React.useEffect(() => {
    if (!commentsDialogOpen || !selectedTicketForComments) return;

    let lastCommentCount = comments.length;

    const pollComments = async () => {
      try {
        let updated = null;
        
        try {
          const res = await GET(`it-dept/tickets?ticketId=${selectedTicketForComments.ticketId}`);
          if (res?.data?.statusCode === 200) {
            const list = res?.data?.data?.paginatedData || res?.data?.data || [];
            updated = Array.isArray(list) ? list.find(t => t.ticketId === selectedTicketForComments.ticketId) : (Array.isArray(list) ? null : list);
          }
        } catch (err) {
          console.log("TicketId filter not supported, fetching all tickets");
        }
        
        if (!updated) {
          const resAll = await GET(`it-dept/tickets`);
          if (resAll?.data?.statusCode === 200) {
            const listAll = resAll?.data?.data?.paginatedData || [];
            updated = listAll.find(t => t.ticketId === selectedTicketForComments.ticketId);
          }
        }
        
        if (updated) {
          const parsedComments = parseComments(updated.commentsJson);
          if (parsedComments.length !== lastCommentCount) {
            setComments(parsedComments);
            setSelectedTicketForComments(updated);
            lastCommentCount = parsedComments.length;
            
            // User is viewing comments - mark all new comments as read
            const readCommentsKey = `readComments_${selectedTicketForComments.ticketId}_${empId}`;
            let readComments = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
            const newCommentTimestamps = parsedComments
              .filter(c => String(c.senderId) !== String(empId))
              .map(c => c.timestamp);
            const allRead = [...new Set([...readComments, ...newCommentTimestamps])];
            localStorage.setItem(readCommentsKey, JSON.stringify(allRead));
            setUnreadCommentCount(0);
            setCommentsReadRefresh(prev => prev + 1); // Trigger re-render to update badges
            
            if (parsedComments.length > 0) {
              setLastReadCommentId(parsedComments[parsedComments.length - 1].timestamp);
            }
          }
        }
      } catch (err) {
        console.error("Error polling comments:", err);
      }
    };

    pollComments();
    const interval = setInterval(pollComments, 5000);

    return () => clearInterval(interval);
  }, [commentsDialogOpen, selectedTicketForComments?.ticketId, empId]);

  const handleOpenComments = async (ticket) => {
    setSelectedTicketForComments(ticket);
    const parsedComments = parseComments(ticket.commentsJson);
    setComments(parsedComments);
    setCommentsDialogOpen(true);
    
    // Mark all comments as read when opening the comments dialog
    const readCommentsKey = `readComments_${ticket.ticketId}_${empId}`;
    const readTimestamps = parsedComments
      .filter(c => String(c.senderId) !== String(empId))
      .map(c => c.timestamp);
    const existingRead = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
    const allRead = [...new Set([...existingRead, ...readTimestamps])];
    localStorage.setItem(readCommentsKey, JSON.stringify(allRead));
    setUnreadCommentCount(0); // Reset unread count for this ticket
    setCommentsReadRefresh(prev => prev + 1); // Trigger re-render to update badges
    if (parsedComments.length > 0) {
      setLastReadCommentId(parsedComments[parsedComments.length - 1].timestamp);
    }
    
    // Update the ticket in the tickets array to trigger re-render of badge
    setTickets(prevTickets => {
      return prevTickets.map(t => {
        if (t.ticketId === ticket.ticketId) {
          // Recalculate unread count for this ticket
          const currentParsedComments = parseComments(t.commentsJson);
          const currentReadComments = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
          const currentUnreadCount = currentParsedComments.filter(c => 
            String(c.senderId) !== String(empId) && 
            !currentReadComments.includes(c.timestamp)
          ).length;
          return {
            ...t,
            __commentCount: currentParsedComments.length,
            __unreadCommentCount: currentUnreadCount
          };
        }
        return t;
      });
    });
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicketForComments) return;
    if (newComment.trim().length > 150) {
      setSnackbar({
        open: true,
        message: "Comment cannot exceed 150 characters",
        severity: "error"
      });
      return;
    }

    setCommentLoading(true);
    try {
      const payload = {
        senderId: parseInt(empId),
        timestamp: new Date().toISOString(),
        content: newComment.trim()
      };

      const response = await POST(`it-dept/tickets/${selectedTicketForComments.ticketId}/comment`, payload);
      
      if (response?.data?.statusCode === 201) {
        const updatedTicket = response.data.data;
        const parsedComments = parseComments(updatedTicket.commentsJson);
        setComments(parsedComments);
        setNewComment("");
        setSelectedTicketForComments(updatedTicket);
        const readCommentsKey = `readComments_${selectedTicketForComments.ticketId}_${empId}`;
        const readComments = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
        const unreadCount = parsedComments.filter(c => 
          String(c.senderId) !== String(empId) && 
          !readComments.includes(c.timestamp)
        ).length;
        setUnreadCommentCount(unreadCount);
        if (parsedComments.length > 0) {
          setLastReadCommentId(parsedComments[parsedComments.length - 1].timestamp);
        }
        setSnackbar({
          open: true,
          message: "Comment added successfully",
          severity: "success"
        });
        await fetchTickets();
      } else {
        setSnackbar({
          open: true,
          message: response?.data?.message || "Failed to add comment",
          severity: "error"
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to add comment",
        severity: "error"
      });
    } finally {
      setCommentLoading(false);
    }
  };

  const getSenderName = (senderId, ticket) => {
    if (String(senderId) === String(empId)) {
      return empName || `IT Admin ${senderId}`;
    }
    if (ticket && String(senderId) === String(ticket.empId)) {
      return ticket.empName || `Employee ${senderId}`;
    }
    if (ticket && String(senderId) === String(ticket.assignedToEmpId)) {
      return ticket.assignedToEmpName || `IT Admin ${senderId}`;
    }
    // Check cache - names should be pre-fetched by useEffect
    return employeeNamesCache[senderId] || `User ${senderId}`;
  };

  const sortedComments = [...comments].sort((a, b) => {
    const aTime = new Date(a.timestamp).getTime();
    const bTime = new Date(b.timestamp).getTime();
    return sortOrder === "newest" ? bTime - aTime : aTime - bTime;
  });

  const handleReassignTicket = () => {
    if (!selectedTicket) return;
    setSelectedNewEmployeeId("");
    setReassignDialogOpen(true);
  };

  const handleConfirmReassign = async () => {
    if (!selectedTicket || !selectedNewEmployeeId) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please select an IT admin to reassign the ticket"
      });
      return;
    }

    setReassignLoading(true);
    try {
      const response = await PUT(`it-dept/tickets/${selectedTicket.ticketId}/reassign`, {
        newAssignedEmpId: parseInt(selectedNewEmployeeId),
        reassignedByEmpId: parseInt(empId)
      });

      if (response?.data?.statusCode === 200) {
        // Track this reassignment locally
        const ticketId = selectedTicket.ticketId;
        const previousAssignedTo = selectedTicket.assignedToEmpId;
        const newAssignedTo = parseInt(selectedNewEmployeeId);
        
        // Only track if reassigned to a different IT admin
        if (previousAssignedTo && String(previousAssignedTo) !== String(newAssignedTo)) {
          setReassignedTickets(prev => {
            const updated = {
              ...prev,
              [ticketId]: {
                reassignedByEmpId: parseInt(empId),
                previousAssignedToEmpId: previousAssignedTo,
                newAssignedToEmpId: newAssignedTo,
                reassignedOn: new Date().toISOString()
              }
            };
            // Store in localStorage for persistence
            try {
              localStorage.setItem('itHelpdesk_reassignedTickets', JSON.stringify(updated));
            } catch (e) {
              console.warn('Failed to store reassigned tickets in localStorage:', e);
            }
            return updated;
          });
        }
        
        setSnackbar({
          open: true,
          severity: "success",
          message: response.data.message || "Ticket reassigned successfully"
        });
        // Update the selected ticket with new data
        if (response.data.data) {
          setSelectedTicket(response.data.data);
        }
        setReassignDialogOpen(false);
        setSelectedNewEmployeeId("");
        await fetchTickets();
      } else {
        setSnackbar({
          open: true,
          severity: "error",
          message: response?.data?.message || "Failed to reassign ticket"
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        severity: "error",
        message: err?.response?.data?.message || "Failed to reassign ticket"
      });
    } finally {
      setReassignLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={isMobile ? 1 : 0.5}>
        

        {/* Filters */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "nowrap",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <MDTypography
                variant="h3"
                gutterBottom
                sx={{ color: "textPrimary", fontFamily: properties.fontFamily, fontWeight: "bold", whiteSpace: "nowrap" }}
                fontSize={isMobile ? "1.2rem" : "16px"}
              >
                IT Helpdesk
              </MDTypography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "nowrap",
                  overflowX: "auto",
                  flex: 1,
                  py: 0.25,
                  px: 0.25,
                }}
              >
                <TextField
                  size="small"
                  label="Employee ID"
                  value={filters.empId}
                  onChange={e => setFilters(prev => ({ ...prev, empId: e.target.value }))}
                  sx={{ minWidth: 120 }}
                />
                <TextField
                  size="small"
                  label="Employee Name"
                  value={filters.empName}
                  onChange={e => setFilters(prev => ({ ...prev, empName: e.target.value }))}
                  sx={{ minWidth: 140 }}
                />
                <TextField
                  size="small"
                  label="Asset ID"
                  value={filters.assetId}
                  onChange={e => setFilters(prev => ({ ...prev, assetId: e.target.value }))}
                  sx={{ minWidth: 120 }}
                />
                <TextField
                  size="small"
                  label="Asset Number"
                  value={filters.assetNo}
                  onChange={e => setFilters(prev => ({ ...prev, assetNo: e.target.value }))}
                  sx={{ minWidth: 120 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filters.status}
                    sx={{ height: 40 }}
                    onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="OPEN">TO DO</MenuItem>
                    <MenuItem value="IN_PROGRESS">IN PROGRESS</MenuItem>
                    <MenuItem value="DONE">DONE</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    label="Priority"
                    value={filters.priority}
                    sx={{ height: 40 }}
                    onChange={e => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="LOW">LOW</MenuItem>
                    <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                    <MenuItem value="HIGH">HIGH</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="contained" sx={{ color: "#fff", whiteSpace: "nowrap" }} size="small" startIcon={<SearchIcon />} onClick={fetchTickets}>
                  Search
                </Button>
                <Tooltip title="Reset filters">
                  <span>
                    <IconButton size="small" onClick={() => setFilters({ empId: "", empName: "", assetId: "", assetNo: "", status: "", priority: "", assignedToEmpId: "", page: 0, size: 50 })}>
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </MDBox>
          </CardContent>
        </Card>

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={10}>
            <Alert severity="info">Loading tickets…</Alert>
          </MDBox>
        ) : error ? (
          <MDBox display="flex" justifyContent="center" py={5}>
            <Alert severity="error">{error}</Alert>
          </MDBox>
        ) : (
          <Grid container spacing={2}>
            <StatusColumn
              title="TO DO"
              tickets={todo.map(t => ({...t, __onView: ticket => {setSelectedTicket(ticket); setDetailsOpen(true);}, __onViewComments: handleOpenComments, __onPriorityChange: handlePriorityMenuOpen}))}
              emptyText="No open tickets"
              color="default"
              onDragOver={handleDragOver}
              onDrop={handleDropToOpen}
              makeDraggable
              onPriorityChange={handlePriorityMenuOpen}
              key={`todo-${commentsReadRefresh}`}
            />
            <StatusColumn
              title="IN PROGRESS"
              tickets={inProgress.map(t => ({...t, __onView: ticket => {setSelectedTicket(ticket); setDetailsOpen(true);}, __onViewComments: handleOpenComments, __onPriorityChange: handlePriorityMenuOpen}))}
              emptyText="No in-progress tickets"
              color="primary"
              onDragOver={handleDragOver}
              onDrop={handleDropToInProgress}
              makeDraggable
              onPriorityChange={handlePriorityMenuOpen}
              key={`inprogress-${commentsReadRefresh}`}
            />
            <StatusColumn
              title="DONE"
              tickets={done.map(t => ({...t, __onView: ticket => {setSelectedTicket(ticket); setDetailsOpen(true);}, __onViewComments: handleOpenComments, __onPriorityChange: handlePriorityMenuOpen}))}
              emptyText="No resolved tickets"
              color="success"
              onDragOver={handleDragOver}
              onDrop={handleDropToDone}
              onPriorityChange={handlePriorityMenuOpen}
              key={`done-${commentsReadRefresh}`}
            />
          </Grid>
        )}

        <Dialog 
          open={detailsOpen} 
          onClose={() => {
            setDetailsOpen(false);
            setSelectedTicket(null);
          }} 
          fullWidth 
          maxWidth="md"
          PaperProps={{
            sx: {
              fontFamily: properties.fontFamily,
              maxHeight: '90vh'
            },
          }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
            <Typography sx={{ fontFamily: properties.fontFamily, fontSize: "16px", fontWeight: 600 }}>
              Ticket #{selectedTicket?.ticketId} - {selectedTicket?.title}
            </Typography>
            <IconButton edge="end" onClick={() => {
              setDetailsOpen(false);
              setSelectedTicket(null);
            }} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent 
            dividers 
            sx={{ 
              overflowY: 'visible', 
              maxHeight: 'none'
            }}
          >
            {selectedTicket && (
              <Box sx={{ overflowY: 'visible', maxHeight: 'none', pb: 1 }}>
                    {/* Basic Info - 4 Column Layout */}
                    <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, 
                    gap: 1.2,
                    fontFamily: properties.fontFamily
                  }}>
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                        Employee
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                        {selectedTicket.empName} ({selectedTicket.empId})
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                        Asset ID
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                        {selectedTicket.assetId ?? "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                        Priority
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                        {selectedTicket.priority ?? "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                        Status
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                        {selectedTicket.status ?? "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                        Created
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                        {selectedTicket.createdOn ? dayjs(selectedTicket.createdOn).format("DD MMM YYYY HH:mm") : "—"}
                      </Typography>
                    </Box>
                    {String(selectedTicket.status||"").toUpperCase()==="IN_PROGRESS" && (selectedTicket.lastModifiedOn || selectedTicket.updatedOn) && (
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                          Updated
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                          {dayjs(selectedTicket.lastModifiedOn || selectedTicket.updatedOn).format("DD MMM YYYY HH:mm")}
                        </Typography>
                      </Box>
                    )}
                    {selectedTicket.resolvedOn && (
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                          Resolved
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                          {dayjs(selectedTicket.resolvedOn).format("DD MMM YYYY HH:mm")}
                        </Typography>
                      </Box>
                    )}
                    {selectedTicket.__isReassigned && selectedTicket.assignedToEmpName ? (
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                          Reassigned To
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, mb: 1, fontFamily: properties.fontFamily }}>
                          {selectedTicket.assignedToEmpName} {selectedTicket.assignedToEmpId ? `(#${selectedTicket.assignedToEmpId})` : ""}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SwapHorizIcon fontSize="small" />}
                          onClick={handleReassignTicket}
                          sx={{
                            textTransform: "none",
                            fontSize: '12px',
                            height: 28,
                            px: 1.5,
                            borderColor: '#1976d2',
                            color: '#1976d2',
                            fontWeight: 500,
                            '&:hover': {
                              borderColor: '#1565c0',
                              backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                          }}
                        >
                          Reassign
                        </Button>
                      </Box>
                    ) : selectedTicket.assignedToEmpName ? (
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                          Assigned To
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, mb: 1, fontFamily: properties.fontFamily }}>
                          {selectedTicket.assignedToEmpName} {selectedTicket.assignedToEmpId ? `(#${selectedTicket.assignedToEmpId})` : ""}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SwapHorizIcon fontSize="small" />}
                          onClick={handleReassignTicket}
                          sx={{
                            textTransform: "none",
                            fontSize: '12px',
                            height: 28,
                            px: 1.5,
                            borderColor: '#1976d2',
                            color: '#1976d2',
                            fontWeight: 500,
                            '&:hover': {
                              borderColor: '#1565c0',
                              backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                          }}
                        >
                          Reassign
                        </Button>
                      </Box>
                    ) : null}
                    {selectedTicket.resolutionComment && (
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                          Resolution
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily }}>
                          {selectedTicket.resolutionComment}
                        </Typography>
                      </Box>
                    )}
                    {selectedTicket.reopenReason && (
                      <Box>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5, fontFamily: properties.fontFamily }}>
                          Reopen Reason
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#F57F17', fontWeight: 500, fontFamily: properties.fontFamily }}>
                          {selectedTicket.reopenReason}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Description Section */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '16px', fontFamily: properties.fontFamily }}>
                    Description
                  </Typography>
                  <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
                    {selectedTicket.issueDescription?.split('\n').map((line, idx) => (
                      line.trim() && (
                        <Typography key={idx} component="li" sx={{ mb: 0.5, fontSize: '14px', color: 'text.primary', fontFamily: properties.fontFamily }}>
                          {line.trim()}
                        </Typography>
                      )
                    )) || (
                      <Typography component="li" sx={{ fontSize: '14px', color: 'text.secondary', fontFamily: properties.fontFamily }}>
                        {selectedTicket.issueDescription || "No description provided"}
                      </Typography>
                    )}
                  </Box>
                  {selectedTicket.issueImage && selectedTicket.imageType && (
                    <Box sx={{mt: 2, display: 'flex', justifyContent: 'flex-start'}}>
                      <Box
                        component="div"
                        sx={{
                          maxWidth: "300px",
                          maxHeight: "200px",
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: "pointer",
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: 2
                          }
                        }}
                      >
                        <img
                          alt="Issue"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block"
                          }}
                          src={`data:${selectedTicket.imageType};base64,${selectedTicket.issueImage}`}
                          onClick={(e) => {
                            // Open full size in new window on click
                            const newWindow = window.open();
                            if (newWindow) {
                              newWindow.document.write(`
                                <html>
                                  <head><title>Issue Image</title></head>
                                  <body style="margin:0; display:flex; justify-content:center; align-items:center; min-height:100vh; background:#f5f5f5;">
                                    <img src="${e.target.src}" style="max-width:90vw; max-height:90vh; border-radius:8px;" />
                                  </body>
                                </html>
                              `);
                            }
                          }}
                        />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 2 }}>
            <Button
              onClick={() => {
                setDetailsOpen(false);
                setSelectedTicket(null);
              }}
              variant="outlined"
              sx={{ 
                textTransform: "none",
                px: 3,
                py: 0.75,
                border: "1px solid #d0d0d0",
                borderRadius: "8px",
                bgcolor: "white",
                color: "#424242",
                fontWeight: 500,
                ml: "auto",
                boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                "&:hover": {
                  borderColor: "#9e9e9e",
                  bgcolor: "#f5f5f5",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Comments Dialog */}
        <Dialog
          open={commentsDialogOpen}
          onClose={() => {
            setCommentsDialogOpen(false);
            setSelectedTicketForComments(null);
            setComments([]);
            setNewComment("");
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              fontFamily: properties.fontFamily,
              maxHeight: '90vh'
            },
          }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
            <Typography sx={{ fontFamily: properties.fontFamily, fontSize: "16px", fontWeight: 600 }}>
              Comments - Ticket #{selectedTicketForComments?.ticketId} • {selectedTicketForComments?.title}
            </Typography>
            <IconButton edge="end" onClick={() => {
              setCommentsDialogOpen(false);
              setSelectedTicketForComments(null);
              setComments([]);
              setNewComment("");
            }} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ overflowY: 'auto', maxHeight: 'calc(90vh - 180px)', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {selectedTicketForComments && (
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '400px' }}>
                {/* Activity Section */}
                <Box sx={{ mb: 1.5, flexShrink: 0 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px', fontFamily: properties.fontFamily }}>
                      Activity ({comments.length})
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
                      startIcon={<SortIcon fontSize="small" />}
                      sx={{ 
                        textTransform: "none",
                        fontSize: '13px',
                        height: 32,
                        px: 1.5,
                        borderColor: '#d0d0d0',
                        color: '#424242',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#9e9e9e',
                          bgcolor: '#f5f5f5'
                        }
                      }}
                    >
                      {sortOrder === "newest" ? "Newest first" : "Oldest first"}
                    </Button>
                  </Box>
                </Box>

                {/* Comments List - Scrollable Area */}
                <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: 1, minHeight: 0, pb: 2, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {sortedComments.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 1 }}>No comments yet. Be the first to comment!</Alert>
                  ) : (
                    <Stack spacing={2.5}>
                      {sortedComments.map((comment, idx) => {
                        const senderName = getSenderName(comment.senderId, selectedTicketForComments);
                        const isEdited = comment.edited || false;
                        const readCommentsKey = `readComments_${selectedTicketForComments.ticketId}_${empId}`;
                        const readComments = JSON.parse(localStorage.getItem(readCommentsKey) || '[]');
                        const isUnread = String(comment.senderId) !== String(empId) && !readComments.includes(comment.timestamp);
                        let formattedTime;
                        const timestampStr = comment.timestamp;
                        try {
                          let commentTime;
                          if (typeof timestampStr === 'string') {
                            if (timestampStr.endsWith('Z')) {
                              commentTime = dayjs.utc(timestampStr).local();
                            } else if (timestampStr.includes('T') && timestampStr.match(/[+-]\d{2}:\d{2}$/)) {
                              commentTime = dayjs(timestampStr);
                            } else if (timestampStr.includes('T')) {
                              commentTime = dayjs.utc(timestampStr).local();
                            } else {
                              commentTime = dayjs(timestampStr);
                            }
                          } else {
                            commentTime = dayjs(timestampStr);
                          }
                          
                          if (commentTime.isValid()) {
                            formattedTime = commentTime.format("MMMM D, YYYY [at] h:mm A");
                          } else {
                            const dateObj = new Date(timestampStr);
                            if (!isNaN(dateObj.getTime())) {
                              formattedTime = dayjs(dateObj).format("MMMM D, YYYY [at] h:mm A");
                            } else {
                              formattedTime = timestampStr;
                            }
                          }
                        } catch (e) {
                          console.error("Error parsing timestamp:", e, timestampStr);
                          try {
                            const dateObj = new Date(timestampStr);
                            if (!isNaN(dateObj.getTime())) {
                              formattedTime = dayjs(dateObj).format("MMMM D, YYYY [at] h:mm A");
                            } else {
                              formattedTime = timestampStr;
                            }
                          } catch (e2) {
                            formattedTime = timestampStr;
                          }
                        }
                        return (
                          <Box 
                            key={idx} 
                            sx={{ 
                              display: "flex", 
                              gap: 1.5, 
                              alignItems: "flex-start",
                              p: 1.5,
                              borderRadius: 1
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: getAvatarColor(senderName),
                                fontSize: '13px',
                                fontWeight: 600,
                                flexShrink: 0
                              }}
                            >
                              {getInitials(senderName)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: 'text.primary', fontFamily: properties.fontFamily }}>
                                  {senderName}
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: 'text.secondary', fontFamily: properties.fontFamily }}>
                                  {formattedTime}
                                </Typography>
                                {isEdited && (
                                  <Chip
                                    label="Edited"
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: '11px',
                                      bgcolor: '#e8f5e9',
                                      color: '#2e7d32',
                                      fontWeight: 500
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography sx={{ fontSize: '14px', color: 'text.primary', mb: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: properties.fontFamily }}>
                                {comment.content}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider', position: 'sticky', bottom: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flex: 1, maxWidth: 'calc(100% - 120px)' }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: getAvatarColor(empName),
                  fontSize: '13px',
                  fontWeight: 600,
                  flexShrink: 0
                }}
              >
                {getInitials(empName)}
              </Avatar>
              <Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={3}
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 150) {
                      setNewComment(value);
                    }
                  }}
                  variant="outlined"
                  size="small"
                  inputProps={{ maxLength: 150 }}
                  helperText={`${newComment.length}/150 characters`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      py: 0.5,
                      minHeight: '40px',
                      fontFamily: properties.fontFamily,
                      '& textarea': {
                        py: 1,
                        lineHeight: 1.5
                      },
                      '&:hover fieldset': {
                        borderColor: '#1976d2'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                        borderWidth: 2
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '11px',
                      mt: 0.5,
                      ml: 0.5,
                      fontFamily: properties.fontFamily
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (newComment.trim() && !commentLoading) {
                        handleAddComment();
                      }
                    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      if (newComment.trim() && !commentLoading) {
                        handleAddComment();
                      }
                    }
                  }}
                />
                <IconButton
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || commentLoading}
                  sx={{
                    bgcolor: newComment.trim() ? '#1976d2' : 'transparent',
                    color: newComment.trim() ? '#fff' : '#9e9e9e',
                    width: 40,
                    height: 40,
                    mt: 0.5,
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: newComment.trim() ? '#1565c0' : 'rgba(0,0,0,0.04)',
                    },
                    '&:disabled': {
                      bgcolor: 'transparent',
                      color: '#bdbdbd'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {commentLoading ? (
                    <CircularProgress size={16} sx={{ color: '#fff' }} />
                  ) : (
                    <SendIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </Box>
            <Button
              onClick={() => {
                setCommentsDialogOpen(false);
                setSelectedTicketForComments(null);
                setComments([]);
                setNewComment("");
              }}
              variant="outlined"
              sx={{ 
                textTransform: "none",
                px: 3,
                py: 0.75,
                border: "1px solid #d0d0d0",
                borderRadius: "8px",
                bgcolor: "white",
                color: "#424242",
                fontWeight: 500,
                '&:hover': {
                  borderColor: "#9e9e9e",
                  bgcolor: "#f5f5f5",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={closeDialogOpen} onClose={() => {setCloseDialogOpen(false); setTicketToClose(null); setResolutionComment("");}} fullWidth maxWidth="sm">
          <DialogTitle>Close Ticket {ticketToClose ? `#${ticketToClose.ticketId}` : ""}</DialogTitle>
          <DialogContent dividers>
            {ticketToClose && (
              <Stack spacing={2} sx={{mt: 1}}>
                <Typography variant="body2" sx={{fontWeight: 600}}>Title: {ticketToClose.title}</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Resolution Comment"
                  value={resolutionComment}
                  onChange={e => setResolutionComment(e.target.value)}
                  placeholder="Enter resolution comment (e.g., Replaced the faulty RAM module and verified boot.)"
                  required
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {setCloseDialogOpen(false); setTicketToClose(null); setResolutionComment("");}}>Cancel</Button>
            <Button onClick={handleCloseTicket} sx={{ color: "#fff" }} variant="contained" color="primary" disabled={!resolutionComment.trim()}>
              Close Ticket
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reassign Ticket Dialog */}
        <Dialog
          open={reassignDialogOpen}
          onClose={() => {
            setReassignDialogOpen(false);
            setSelectedNewEmployeeId("");
          }}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              fontFamily: properties.fontFamily,
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontFamily: properties.fontFamily, fontSize: "16px" }}
            >
              Reassign Ticket #{selectedTicket?.ticketId}
            </Typography>
            <IconButton edge="end" onClick={() => {
              setReassignDialogOpen(false);
              setSelectedNewEmployeeId("");
            }} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedTicket && (
              <Stack spacing={2.5} sx={{ mt: 1 }}>
                <DialogContentText sx={{ fontFamily: properties.fontFamily }}>
                  Select a new IT admin to reassign ticket &quot;{selectedTicket.title}&quot;
                </DialogContentText>
                <Box>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 1, fontFamily: properties.fontFamily }}>
                    Currently Assigned To
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: 'text.primary', fontWeight: 500, fontFamily: properties.fontFamily, mb: 2 }}>
                    {selectedTicket.assignedToEmpName || "—"} {selectedTicket.assignedToEmpId ? `(#${selectedTicket.assignedToEmpId})` : ""}
                  </Typography>
                </Box>
                <FormControl fullWidth size="small">
                  <InputLabel>Select New IT Admin *</InputLabel>
                  <Select
                    value={selectedNewEmployeeId}
                    onChange={(e) => setSelectedNewEmployeeId(e.target.value)}
                    label="Select New IT Admin *"
                    disabled={reassignLoading}
                    sx={{
                      height:40,
                      fontFamily: properties.fontFamily,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: 2
                      }
                    }}
                  >
                    {employeesList.length === 0 ? (
                      <MenuItem disabled sx={{ fontFamily: properties.fontFamily }}>
                        No IT Admin employees available
                      </MenuItem>
                    ) : (
                      employeesList
                        .filter(emp => String(emp.empId) !== String(selectedTicket.assignedToEmpId))
                        .map((employee) => (
                          <MenuItem key={employee.empId} value={employee.empId} sx={{ fontFamily: properties.fontFamily }}>
                            {employee.employeeName} (#{employee.empId})
                          </MenuItem>
                        ))
                    )}
                  </Select>
                </FormControl>
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, gap: 1.5 }}>
            <Button
              onClick={() => {
                setReassignDialogOpen(false);
                setSelectedNewEmployeeId("");
              }}
              variant="outlined"
              size="medium"
              disabled={reassignLoading}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                px: 3,
                py: 1,
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'text.secondary',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReassign}
              variant="contained"
              size="medium"
              disabled={reassignLoading || !selectedNewEmployeeId}
              startIcon={reassignLoading ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1,
                color:'#fff',
                backgroundColor: 'primary',
                '&:hover': {
                  backgroundColor: 'primary'
                },
                '&:disabled': {
                  backgroundColor: 'action.disabledBackground',
                  color: 'action.disabled'
                }
              }}
            >
              {reassignLoading ? "Reassigning..." : "Reassign Ticket"}
            </Button>
          </DialogActions>
        </Dialog>

        <Menu
          anchorEl={priorityMenuAnchor}
          open={Boolean(priorityMenuAnchor)}
          onClose={handlePriorityMenuClose}
        >
          <MenuItem onClick={() => handleChangePriority("HIGH")}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
              <ArrowUpwardIcon sx={{color: "error.main", fontSize: 18}} />
              <Typography>HIGH</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleChangePriority("MEDIUM")}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
              <RemoveIcon sx={{color: "warning.main", fontSize: 18}} />
              <Typography>MEDIUM</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleChangePriority("LOW")}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
              <ArrowDownwardIcon sx={{color: "text.secondary", fontSize: 18}} />
              <Typography>LOW</Typography>
            </Box>
          </MenuItem>
        </Menu>

        <Snackbar
          open={snackbar.open}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity === "success" ? "success" : snackbar.severity === "error" ? "error" : "info"}
            variant="filled"
            sx={{width: "100%", color: "#fff", fontFamily: properties.fontFamily}}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}


